import { useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { today, getLocalTimeZone } from "@internationalized/date";
import { Period } from "../../generated/core-api";

const VALID_PERIODS = new Set<string>(Object.values(Period));
const DEFAULT_PERIOD: Period = "weekly";

function isValidPeriod(value: string): value is Period {
  return VALID_PERIODS.has(value);
}

function daysAgo(days: number): string {
  return today(getLocalTimeZone()).subtract({ days }).toString();
}

function todayISO(): string {
  return today(getLocalTimeZone()).toString();
}

export interface DateRangeState {
  fromDate: string;
  toDate: string;
  period: Period;
  isDatePickerOpen: boolean;
  setFromDate: (from: string) => void;
  setToDate: (to: string) => void;
  setDateRange: (from: string | null, to: string | null) => void;
  setPeriod: (period: Period) => void;
  setDatePickerOpen: (open: boolean) => void;
}

export function useDateRange(): DateRangeState {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFrom = searchParams.get("from") ?? daysAgo(90);
  const initialTo = searchParams.get("to") ?? todayISO();
  const periodParam = searchParams.get("period");
  const initialPeriod: Period =
    periodParam && isValidPeriod(periodParam) ? periodParam : DEFAULT_PERIOD;

  const [fromDate, setFromDateState] = useState(initialFrom);
  const [toDate, setToDateState] = useState(initialTo);
  const [period, setPeriodState] = useState<Period>(initialPeriod);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  const updateSearchParams = useCallback(
    (from: string, to: string, p: Period) => {
      setSearchParams(
        (prev) => {
          prev.set("from", from);
          prev.set("to", to);
          prev.set("period", p);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFromDate = useCallback(
    (from: string) => {
      setFromDateState(from);
      updateSearchParams(from, toDate, period);
    },
    [toDate, period, updateSearchParams],
  );

  const setToDate = useCallback(
    (to: string) => {
      setToDateState(to);
      updateSearchParams(fromDate, to, period);
    },
    [fromDate, period, updateSearchParams],
  );

  const setDateRange = useCallback(
    (from: string | null, to: string | null) => {
      const newFrom = from ?? daysAgo(90);
      const newTo = to ?? todayISO();
      setFromDateState(newFrom);
      setToDateState(newTo);
      updateSearchParams(newFrom, newTo, period);
    },
    [period, updateSearchParams],
  );

  const setPeriod = useCallback(
    (p: Period) => {
      setPeriodState(p);
      updateSearchParams(fromDate, toDate, p);
    },
    [fromDate, toDate, updateSearchParams],
  );

  return useMemo(
    () => ({
      fromDate,
      toDate,
      period,
      isDatePickerOpen,
      setFromDate,
      setToDate,
      setDateRange,
      setPeriod,
      setDatePickerOpen,
    }),
    [
      fromDate,
      toDate,
      period,
      isDatePickerOpen,
      setFromDate,
      setToDate,
      setDateRange,
      setPeriod,
      setDatePickerOpen,
    ],
  );
}
